"use client";

import { getSlugsByContent } from "@/components/Breadcrumb";
import ClientSWRConfig from "@/components/ClientSWRConfig";
import { InnerContainer, MainContainer } from "@/components/Containers";
import { Flex, FlexColumn } from "@/components/Flex";
import Footer from "@/components/Footer";
import LogPageView from "@/components/LogPageVisit";
import Mountable from "@/components/styled/div/Mountable";
import { TextToSpeechContextProvider } from "@/contexts/TextToSpeechContext";
import { ReadonlyChatInterface } from "@/features/AI/KaiChat/ReadonlyChatInterface";
import { ChatEditorCommunicatorProvider } from "@/features/AI/KaiChat/hooks/useChatEditorCommunicator";
import { resolveFlashcardSetSWRKey } from "@/hooks/flashcards/utils";
import br from "@/styles/breakpoints.module.css";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { ReviewModeContextProvider } from "@knowt/syncing/context/FlashcardReviewModeContext";
import {
    AccountType,
    FlashcardSet,
    FlashcardSetViewer,
    ItemType,
    Note,
    StudySession,
    SubscriptionType,
} from "@knowt/syncing/graphql/schema";
import { StableSWRKeys } from "@knowt/syncing/hooks/swr/swr";
import { LocalUser } from "@knowt/syncing/hooks/user/types";
import { usePublicProfile } from "@knowt/syncing/hooks/user/useUserData";
import { TIME_SECONDS, now } from "@knowt/syncing/utils/dateTimeUtils";
import { useEffect, useMemo, useState } from "react";
import { unstable_serialize } from "swr";
import { HorizontallyCenteredRepeatableLeaderboardAdSlot } from "../Ads/components/FreestarAdSlot";
import MainContainerAdSlot from "../Ads/components/MainContainerAdSlot";
import ExploreTopUserContents from "../LandingPages/components/ExploreTopUserContents";
import { RateExtensionPopup } from "./components/RateExtensionPopup";
import SetOverviewFlashcardList from "./components/SetOverviewFlashcardList";
import SetOverviewStudyBtnWithCarousel from "./components/SetOverviewStudyBtnWithCarousel";
import SetOverviewTagsDescription from "./components/SetOverviewTagsDescription";
import SetOverviewWithSignupPopup from "./components/popups/SetOverviewWithSignupPopup";
import AddRemoveTermBtn from "./components/styled/AddRemoveTermBtn";
import SetOverviewCourseBreadcrumb from "./components/styled/SetOverviewCourseBreadcrumb";
import SetOverviewOwnerCard from "./components/styled/SetOverviewOwnerCard";
import SetOverviewStudyingProgress from "./components/styled/SetOverviewStudyingProgress";
import TrackFlashcardSetViewEffect from "./effects/TrackFlashcardSetViewEffect";
import SetOverviewHeader from "./components/SetOverviewHeader";
import { SetOverviewViewStates } from "./types";
import SetProgressTable from "../UserContentManagement/ClassPage/TeacherView/components/ClassMaterialProgressStats/components/SetProgressStats/SetProgressStatsTable";
import Breadcrumb from "@/components/Breadcrumb";
import { useFolderAncestors } from "@knowt/syncing/hooks/folders/useFolders";

const FLASHCARD_FROM_QUIZLET_COUNTER = "FLASHCARD_FROM_QUIZLET_COUNTER";

// states are:
// normal user seeing his own flashcard
// normal user seeing another user's flashcard
// teacher in class flashcard
// student in class seeings flashcard

type PageProps = {
    serverUser: LocalUser;
    serverFlashcardSet: FlashcardSet;
    serverFlashcardSetViewer: FlashcardSetViewer;
    serverStudySession?: StudySession | null | undefined;
    topNotes?: Note[];
    topFlashcardSets?: FlashcardSet[];
};

const FlashcardSetOverviewPage = ({
    serverUser,
    serverFlashcardSetViewer,
    serverStudySession,
    topNotes,
    topFlashcardSets,
    serverFlashcardSet,
}: PageProps) => {
    const { flashcardSetId } = serverFlashcardSet;
    const user = serverUser?.user;
    const userId = user?.ID;

    return (
        <ClientSWRConfig
            value={{
                fallback: {
                    [StableSWRKeys.USER]: serverUser,
                    [unstable_serialize(resolveFlashcardSetSWRKey({ flashcardSetId }))]: serverFlashcardSet,
                    [unstable_serialize(["flashcardSetViewer", userId, flashcardSetId])]: serverFlashcardSetViewer,
                    [unstable_serialize(["study-session", userId, flashcardSetId])]: serverStudySession,
                },
            }}>
            <ChatEditorCommunicatorProvider
                itemType={ItemType.FLASHCARDSET}
                itemId={flashcardSetId}
                isOwner={serverFlashcardSet.userId === userId}>
                <SWRComponents
                    serverUser={serverUser}
                    serverFlashcardSet={serverFlashcardSet}
                    serverFlashcardSetViewer={serverFlashcardSetViewer}
                    serverStudySession={serverStudySession}
                    topNotes={topNotes}
                    topFlashcardSets={topFlashcardSets}
                />
            </ChatEditorCommunicatorProvider>
        </ClientSWRConfig>
    );
};

const getImportedQuizletCounter = (counterRaw: string | null) => {
    if (!counterRaw) return 0;
    const parsed = Number.parseInt(counterRaw, 10);
    if (Number.isNaN(parsed)) return 0;
    return parsed;
};

export const SWRComponents = ({
    serverUser,
    serverFlashcardSetViewer,
    serverStudySession,
    topNotes,
    topFlashcardSets,
    serverFlashcardSet,
}: PageProps) => {
    const { flashcardSetId } = serverFlashcardSet;
    const user = serverUser?.user;
    const ownerId = serverFlashcardSet.userId;
    const readOnly = !!(user?.ID !== ownerId || serverFlashcardSet.lock);
    const isTeacher = user?.accountType === AccountType.Teacher;
    const isStudent = user?.accountType === AccountType.Student;
    const userId = user?.ID;

    const isTeacherClassSet = isTeacher && !!serverFlashcardSet.classId;

    const setViewStates: SetOverviewViewStates = {
        isReadOnly: readOnly,
        isClassSet: !!serverFlashcardSet?.classId,
        isTeacherViewClassSet: isTeacher && !!serverFlashcardSet.classId && !readOnly,
        isStudentViewClassSet: isStudent && !!serverFlashcardSet.classId, // TODO: should probably detect if this is his class
        isNormalUserViewOwnSet: !readOnly,
        isNormalUserViewOtherSet: readOnly,
    };

    const [isRateExtensionOpen, setIsRateExtensionOpen] = useState(false);

    const { userData: ownerData, subscriptionType } = usePublicProfile(ownerId);

    const { ancestors } = useFolderAncestors({ folderId: serverFlashcardSet.folderId });

    const { quizletUrl, created } = serverFlashcardSet;

    useEffect(() => {
        // Created in the last 30 seconds
        if (quizletUrl && typeof created === "number" && created > now() - 30 * TIME_SECONDS.SECOND) {
            const counterRaw = localStorage.getItem(FLASHCARD_FROM_QUIZLET_COUNTER);
            const newCounter = getImportedQuizletCounter(counterRaw) + 1;
            localStorage.setItem(FLASHCARD_FROM_QUIZLET_COUNTER, newCounter.toString());
            if (newCounter === 2) {
                setIsRateExtensionOpen(true);
                Mixpanel.track("Chrome Extension Rating Popup - Shown");
            }
        }
    }, [created, quizletUrl]);

    const teacherHasPremium =
        serverFlashcardSet.classId &&
        (subscriptionType === SubscriptionType.TEACHER_LIMITLESS || subscriptionType === SubscriptionType.TEACHER_PRO);

    const breadcrumbItems = useMemo(() => {
        const slugs = getSlugsByContent({ flashcardSet: serverFlashcardSet });
        if (slugs.length > 0) return slugs;

        return [
            ...(ancestors?.map(ancestorFolder => ({
                label: ancestorFolder.name,
                href: `/folder/${ancestorFolder.folderId}`,
            })) ?? []),
            { label: serverFlashcardSet.title },
        ] as {
            label: string;
            href?: string;
            img?: string;
        }[];
    }, [ancestors, serverFlashcardSet.title, serverFlashcardSet]);

    return (
        <>
            <LogPageView
                page="Flashcard Set - Visited"
                props={{
                    flashcardSetId,
                    mediaId: serverFlashcardSet.mediaId,
                    noteId: serverFlashcardSet.noteId,
                    isOwner: serverFlashcardSet.userId === userId,
                    exam: serverFlashcardSet.exam_v2,
                    examUnit: serverFlashcardSet.examUnit,
                    subject: serverFlashcardSet.subject,
                    topic: serverFlashcardSet.topic,
                    classId: serverFlashcardSet.classId,
                }}
            />
            <TrackFlashcardSetViewEffect flashcardSetId={flashcardSetId} />
            <Mountable mount={!serverUser.user}>
                <SetOverviewWithSignupPopup />
            </Mountable>
            <RateExtensionPopup isOpen={isRateExtensionOpen} onClose={() => setIsRateExtensionOpen(false)} />

            <MainContainer
                style={{
                    marginInline: "4rem 4rem",
                    marginBlock: "2rem 2rem",
                    gap: "4rem",
                    overflow: "hidden",
                }}>
                <InnerContainer style={{ position: "relative", overflow: "hidden", maxWidth: "100rem", gap: "2rem" }}>
                    {/* BREADCRUMB */}
                    {serverFlashcardSet?.classId ? (
                        <SetOverviewCourseBreadcrumb classId={serverFlashcardSet.classId} />
                    ) : (
                        <Breadcrumb className={br.mdDownFullWidthScrollableX} root="home" items={breadcrumbItems} />
                    )}

                    <SetOverviewHeader flashcardSet={serverFlashcardSet} setViewStates={setViewStates} />

                    <TextToSpeechContextProvider flashcardSetId={flashcardSetId}>
                        <ReviewModeContextProvider flashcardSet={serverFlashcardSet} flashcardSetId={flashcardSetId}>
                            <SetOverviewStudyBtnWithCarousel
                                userId={userId || undefined}
                                flashcardSetId={serverFlashcardSet.flashcardSetId}
                                setViewStates={setViewStates}
                            />
                        </ReviewModeContextProvider>

                        {/* STUDENT SET DETAILS */}
                        <Flex style={{ gap: "2rem" }} className={br.smDownColumn}>
                            <Mountable mount={setViewStates.isTeacherViewClassSet || setViewStates.isReadOnly}>
                                <SetOverviewOwnerCard ownerData={ownerData} userId={userId as string | undefined} />
                            </Mountable>
                            <SetOverviewTagsDescription
                                flashcardSet={serverFlashcardSet}
                                readOnly={readOnly}
                                fromOverViewPage
                            />
                        </Flex>

                        <HorizontallyCenteredRepeatableLeaderboardAdSlot
                            className={br.mdDownDisplayNone}
                            containerStyle={{ marginBottom: "4rem" }}
                            isEnabled={!teacherHasPremium}
                        />

                        <SetProgressTable
                            classId={serverFlashcardSet.classId}
                            flashcardSet={serverFlashcardSet}
                            selectedSection={null}
                            singleUserId={userId}
                            isTeacherView={false}
                        />

                        <Mountable mount={!isTeacher && !!userId}>
                            <SetOverviewStudyingProgress
                                flashcardSet={serverFlashcardSet}
                                userId={userId as string}
                                serverStudySession={serverStudySession}
                            />
                        </Mountable>

                        <SetOverviewFlashcardList
                            readOnly={readOnly}
                            initialFlashcardSet={serverFlashcardSet}
                            isTeacherClassSet={isTeacherClassSet}
                            teacherHasPremium={teacherHasPremium}
                            serverUser={serverUser}
                            serverFlashcardSetViewer={serverFlashcardSetViewer}
                            serverStudySession={serverStudySession}
                        />
                    </TextToSpeechContextProvider>

                    <Mountable mount={!readOnly}>
                        <div style={{ paddingBottom: "4rem" }} />
                        <AddRemoveTermBtn flashcardSetId={flashcardSetId} />
                    </Mountable>

                    <div style={{ paddingBottom: "4rem" }} />
                    <FlexColumn style={{ gap: "3.2rem" }}>
                        {topNotes?.length ? <ExploreTopUserContents notes={topNotes} /> : null}
                        {topFlashcardSets?.length ? <ExploreTopUserContents flashcardSets={topFlashcardSets} /> : null}
                    </FlexColumn>
                    <ReadonlyChatInterface
                        flashcardSet={serverFlashcardSet}
                        floatStyles={{
                            position: "fixed",
                            zIndex: 9999,
                        }}
                    />
                </InnerContainer>

                <MainContainerAdSlot {...serverUser} isEnabled={!teacherHasPremium} />
            </MainContainer>

            <Footer ad={!teacherHasPremium} />

            {/* <NewlyCreatedFlashcardSetSharePopup
                flashcardSetId={flashcardSetId}
                isTeacherClassSet={isTeacherClassSet}
                isOwner={serverUser.user?.ID === serverFlashcardSet.userId}
            /> */}
        </>
    );
};

export default FlashcardSetOverviewPage;
